import * as schema from '_redux/schema';
import requestHandler from '_services/api/axios-config';
import {HttpMethods} from '_services/api/axios.types';
// eslint-disable-next-line import/no-extraneous-dependencies
import log from 'loglevel';
import {normalize} from 'normalizr';
import {toast} from 'react-toastify';
import {SagaIterator} from 'redux-saga';
import {call, put} from 'redux-saga/effects';
import {
  CancelAppointmentStartAction,
  ExportAppointmentsCsvStartAction,
  ExportAppointmentsAsyncStartAction,
  FetchAllAppointmentsStartAction,
  FetchAppointmentActivityLogsStartAction,
  FetchPaginatedClientsStartAction,
  FetchPaginatedProvidersStartAction,
  FetchProviderAvailabilityStartAction,
  ScheduleAppointmentStartAction,
  UpdateAppointmentStartAction,
} from './calendar-actions.types';
import {
  cancelAppointmentFailure,
  cancelAppointmentSuccess,
  exportAppointmentsCsvFailure,
  exportAppointmentsCsvSuccess,
  exportAppointmentsAsyncSuccess,
  exportAppointmentsAsyncFailure,
  exportAppointmentsProgressUpdate,
  exportAppointmentsPollingComplete,
  fetchAllAppointmentsFailure,
  fetchAllAppointmentsSuccess,
  // Activity Logs
  fetchAppointmentActivityLogsFailure,
  fetchAppointmentActivityLogsSuccess,
  fetchPaginatedClientsFailure,
  fetchPaginatedClientsSuccess,
  fetchPaginatedProvidersFailure,
  fetchPaginatedProvidersSuccess,
  fetchProviderAvailabilityFailure,
  fetchProviderAvailabilitySuccess,
  scheduleAppointmentFailure,
  scheduleAppointmentSuccess,
  updateAppointmentFailure,
  updateAppointmentSuccess,
} from './calendar.actions';
import {
  CancelAppointmentResponse,
  FetchAllAppointmentsResponse,
  FetchedClientsResponse,
  NormalizedFetchedPrescribers,
  NormalizedFetchedTherapists,
  ProvidersModelTypes,
  ScheduleAppointmentResponse,
  UpdateAppointmentResponse,
  ExportAsyncResponse,
  ExportProgressResponse,
  ExportStatus,
} from './calendar.types';

export function* fetchProviderAvailabilityStartSaga({
  payload: {
    providerId,
    appointmentType,
    eventTypeTitle,
    yearAndMonth,
    duration,
    calendarId,
    state,
    callback,
  },
}: FetchProviderAvailabilityStartAction): SagaIterator<void> {
  try {
    const queryParams = new URLSearchParams({
      yearAndMonth,
      duration,
      appointmentType,
      providerId,
      ...(eventTypeTitle && {eventTypeTitle}),
      ...(state && {state}),
    });
    const url = `/api/admin/calendar/availability/${calendarId}?${queryParams.toString()}`;
    const {
      data: {message},
    } = yield call(requestHandler, {
      method: HttpMethods.GET,
      url,
    });
    yield put(fetchProviderAvailabilitySuccess({providerId, ...message}));
    if (callback) yield call(() => callback(message));
  } catch (error) {
    yield put(fetchProviderAvailabilityFailure());
    if (callback) yield call(() => callback({}));
    log.warn(error);
  }
}

export function* fetchPaginatedProvidersSaga({
  payload: {
    countryCode,
    searchKeywords,
    page,
    maxResults,
    providerModel,
    callback,
  },
}: FetchPaginatedProvidersStartAction): SagaIterator<void> {
  try {
    const isPrescriber = providerModel === ProvidersModelTypes.prescribers;
    const url = isPrescriber
      ? '/api/admin/prescriber/paginated-prescribers'
      : '/api/admin/therapist/paginated-therapists';

    const {
      data: {message},
    }: {
      data: {
        message: NormalizedFetchedTherapists | NormalizedFetchedPrescribers;
      };
    } = yield call(requestHandler, {
      method: HttpMethods.GET,
      url: `${url}?searchKeywords=${searchKeywords}&maxResults=${maxResults}&page=${page}${
        countryCode ? `&countryCode=${countryCode}` : ''
      }`,
    });

    const providersSchema = isPrescriber
      ? schema.arrayOfPrescribers
      : schema.arrayOfTherapists;
    const normalizedTherapists = yield call(
      normalize,
      message.result, // for therapist Therapists[], for prescribers Prescribers[]
      providersSchema,
    );

    // Ensure the entities structure is always present
    if (!normalizedTherapists.entities[providerModel]) {
      normalizedTherapists.entities[providerModel] = {};
    }

    callback?.(false);
    yield put(
      fetchPaginatedProvidersSuccess({
        ...normalizedTherapists,
        filter: providerModel,
        pagination: message,
      }),
    );
  } catch (error) {
    callback?.(false);
    yield put(fetchPaginatedProvidersFailure());
  }
}

// Fetch all appointments
export function* fetchAllAppointmentsStartSaga({
  payload: {
    providerId,
    searchTerm,
    from,
    to,
    maxItems,
    page,
    filter = false,
    country,
    timezone,
    callback,
  },
}: FetchAllAppointmentsStartAction): SagaIterator<void> {
  const queryKeys = {searchTerm, providerId, from, to, country, timezone};
  try {
    const url = `/api/admin/calendar/sessions?providerId=${providerId}&from=${from}&to=${to}&searchTerm=${searchTerm}&maxItems=${maxItems}&page=${page}&country=${country}&timezone=${timezone}`;
    const {
      data: {message},
    }: {
      data: {
        message: FetchAllAppointmentsResponse;
      };
    } = yield call(requestHandler, {
      method: HttpMethods.GET,
      url,
    });
    const results = {
      ...message,
      queryKeys,
      filter,
    };
    yield put(fetchAllAppointmentsSuccess(results));
    if (callback) yield call(callback);
  } catch (error) {
    yield put(fetchAllAppointmentsFailure(queryKeys));
    if (callback) yield call(callback);
    log.warn(error);
  }
}

// Schedule Appointment
export function* scheduleAppointmentStartSaga({
  payload: {
    appointmentDateTimeString,
    prescriberId,
    therapistId,
    patientFullName,
    patientEmail,
    patientPhoneNumber,
    patientCountry,
    patientTimezone,
    appointmentTypeID,
    appointmentType,
    eventTypeTitle,
    callback,
  },
}: ScheduleAppointmentStartAction): SagaIterator<void> {
  try {
    const url = `/api/admin/calendar/schedule`;

    const {
      data: {message},
    }: {
      data: {
        message: ScheduleAppointmentResponse;
      };
    } = yield call(requestHandler, {
      method: HttpMethods.POST,
      url,
      data: {
        appointmentDateTimeString,
        prescriberId,
        therapistId,
        patientFullName,
        patientEmail,
        patientPhoneNumber,
        patientCountry,
        patientTimezone,
        appointmentTypeID,
        appointmentType,
        ...(eventTypeTitle && {eventTypeTitle}),
      },
    });
    yield put(scheduleAppointmentSuccess(message));
    if (callback) yield call(callback);
    toast.success('Appointment scheduled successfully');
  } catch (error) {
    yield put(scheduleAppointmentFailure());
    if (callback) yield call(callback);
    log.warn(error);
  }
}

// Cancel Appointment
export function* cancelAppointmentStartSaga({
  payload: {appointmentID, reasonForCancellation, callback},
}: CancelAppointmentStartAction): SagaIterator<void> {
  try {
    const url = `/api/admin/calendar/session/cancel`;

    const {
      data: {message},
    }: {
      data: {
        message: CancelAppointmentResponse;
      };
    } = yield call(requestHandler, {
      method: HttpMethods.POST,
      url,
      data: {
        appointmentID,
        reasonForCancellation,
      },
    });
    yield put(cancelAppointmentSuccess(message));
    if (callback) yield call(callback);
    toast.success('Appointment cancelled successfully');
  } catch (error) {
    yield put(cancelAppointmentFailure());
    if (callback) yield call(callback);
    log.warn(error);
  }
}

// Update Appointment
export function* updateAppointmentStartSaga({
  payload: {appointmentId, labelId, callback},
}: UpdateAppointmentStartAction): SagaIterator<void> {
  try {
    const url = `/api/admin/calendar/session/update`;

    const {
      data: {message},
    }: {
      data: {
        message: UpdateAppointmentResponse;
      };
    } = yield call(requestHandler, {
      method: HttpMethods.PUT,
      url,
      data: {
        appointmentId,
        labelId,
      },
    });
    yield put(updateAppointmentSuccess(message));
    if (callback) yield call(callback);
    toast.success('Updated appointment label successfully');
  } catch (error) {
    yield put(updateAppointmentFailure());
    if (callback) yield call(callback);
    log.warn(error);
  }
}

export function* exportAppointmentsCsvSaga({
  payload: {startDate, endDate, includeCanceled, timezone, callback},
}: ExportAppointmentsCsvStartAction): SagaIterator<void> {
  try {
    const url = `/api/admin/appointments/export?start=${startDate}&end=${endDate}&timezone=${timezone}&includeCanceled=${
      includeCanceled ? 'yes' : 'no'
    }`;

    const response: {
      data: string | {status: number; message: string};
      headers: {[key: string]: string};
    } = yield call(requestHandler, {
      method: HttpMethods.GET,
      url,
    });

    const contentType =
      response.headers['content-type'] ||
      response.headers['Content-Type'] ||
      '';

    if (!contentType.includes('text/csv')) {
      const jsonData = response.data as {status: number; message: string};
      toast.info(jsonData.message);

      yield put(exportAppointmentsCsvSuccess());
      if (callback) yield call(callback);
      return;
    }

    const csvData = response.data as string;
    const blob = new Blob([csvData], {type: 'text/csv'});
    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `appointments_export_${startDate}_to_${endDate}.csv`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(downloadUrl);

    yield put(exportAppointmentsCsvSuccess());
    if (callback) yield call(callback);
  } catch (error) {
    yield put(exportAppointmentsCsvFailure());
    if (callback) yield call(callback);
    log.warn(error);
  }
}

export function* fetchPaginatedClientsStartSaga({
  payload: {search, page, maxResults, countryCode, callback},
}: FetchPaginatedClientsStartAction): SagaIterator<void> {
  try {
    const url = `/api/admin/patients/paginated-patients?search=${search}&maxResults=${maxResults}&page=${page}&countryCode=${countryCode ??
      ''}`;

    const {
      data: {message},
    }: {
      data: {
        message: FetchedClientsResponse;
      };
    } = yield call(requestHandler, {
      method: HttpMethods.GET,
      url,
    });

    yield put(fetchPaginatedClientsSuccess(message));
    if (callback) yield call(callback);
  } catch (error) {
    yield put(fetchPaginatedClientsFailure());
    if (callback) yield call(callback);
    log.warn(error);
  }
}

export function* fetchAppointmentActivityLogsStartSaga({
  payload: {calcomBookingUid, page, limit, callback},
}: FetchAppointmentActivityLogsStartAction): SagaIterator<void> {
  try {
    const url = `/api/admin/appointment-activity-logs/${calcomBookingUid}?page=${page}&limit=${limit}`;
    const {
      data: {message},
    }: {
      data: {
        message: import('./calendar.types').ActivityLogsResponse;
      };
    } = yield call(requestHandler, {
      method: HttpMethods.GET,
      url,
    });

    yield put(fetchAppointmentActivityLogsSuccess(calcomBookingUid, message));
    if (callback) yield call(() => callback(message));
  } catch (error) {
    yield put(fetchAppointmentActivityLogsFailure({calcomBookingUid}));
    if (callback) yield call(() => callback(undefined));
    log.warn(error);
  }
}

const POLLING_INTERVAL = 3000; // 3 seconds
const MAX_POLLING_DURATION = 420000; // 8 minutes

export function* exportAppointmentsAsyncSaga({
  payload: {startDate, endDate, includeCanceled, timezone},
}: ExportAppointmentsAsyncStartAction): SagaIterator<void> {
  try {
    const url = `/api/admin/appointments/export?start=${startDate}&end=${endDate}&timezone=${timezone}&includeCanceled=${
      includeCanceled ? 'yes' : 'no'
    }`;

    const {
      data: {data: exportData},
    }: {
      data: {
        status: number;
        message: string;
        data: ExportAsyncResponse;
      };
    } = yield call(requestHandler, {
      method: HttpMethods.POST,
      url,
    });

    const {exportId} = exportData;
    yield put(exportAppointmentsAsyncSuccess(exportData));

    const startTime = Date.now();
    let isCompleted = false;

    while (!isCompleted && Date.now() - startTime < MAX_POLLING_DURATION) {
      yield call(delay, POLLING_INTERVAL);

      const progressUrl = `/api/admin/appointments/export/progress?exportId=${exportId}`;
      const {
        data: {message: progressData},
      }: {
        data: {
          status: number;
          message: ExportProgressResponse;
        };
      } = yield call(requestHandler, {
        method: HttpMethods.GET,
        url: progressUrl,
      });

      yield put(exportAppointmentsProgressUpdate(progressData));

      if (progressData.exportStatus === ExportStatus.COMPLETED) {
        isCompleted = true;
        yield put(exportAppointmentsPollingComplete(progressData));

        if (progressData.csvDownloadUrl) {
          const link = document.createElement('a');
          link.href = progressData.csvDownloadUrl;
          link.download = `appointments_export_${startDate}_to_${endDate}_${timezone.replace(
            '/',
            '_',
          )}.csv`;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          toast.success('Appointment export completed successfully!');
        }
      } else if (progressData.exportStatus === ExportStatus.FAILED) {
        isCompleted = true;
        const errorMsg =
          progressData.failedReason || 'Export failed for unknown reason';
        yield put(exportAppointmentsAsyncFailure(errorMsg));
        toast.error(`Export failed: ${errorMsg}`);
      }
    }

    if (!isCompleted) {
      const timeoutMsg = 'Export timed out after 8 minutes';
      yield put(exportAppointmentsAsyncFailure(timeoutMsg));
      toast.error(timeoutMsg);
    }
  } catch (error) {
    yield put(exportAppointmentsAsyncFailure());
    log.warn(error);
    toast.error('Failed to start export job');
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
